import { useState, useEffect, useRef } from "react";

interface DataPoint {
  date: string;
  isSelected?: boolean;
  [key: string]: any;
}

/**
 * Custom hook to manage chart point selection state and interactions
 * Handles selected/hovered points and provides click handler
 */
export function useChartSelection<T extends DataPoint>(data: T[]) {
  const initialSelectedPoint =
    data.find((point) => point.isSelected) || data[data.length - 1];

  const [selectedPoint, setSelectedPoint] = useState<T | undefined>(
    initialSelectedPoint
  );
  const [hoveredPoint, setHoveredPoint] = useState<T | null>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (data.length === 0) {
      setSelectedPoint(undefined);
      setHoveredPoint(null);
      return;
    }
    const newSelectedPoint = data.find((point) => point.isSelected);
    if (newSelectedPoint) {
      setSelectedPoint(newSelectedPoint);
    } else {
      setSelectedPoint((prev) => {
        if (!prev) return data[data.length - 1];
        const matchingPoint = data.find((point) => point.date === prev.date);
        return matchingPoint ?? data[data.length - 1];
      });
    }
  }, [data]);

  const handlePointClick = (clickData: any) => {
    if (clickData && clickData.activePayload) {
      setSelectedPoint(clickData.activePayload[0].payload);
    }
  };

  const activePoint = hoveredPoint || selectedPoint;
  const activeIndex = activePoint
    ? data.findIndex((d) => d.date === activePoint.date)
    : -1;

  return {
    selectedPoint,
    setSelectedPoint,
    hoveredPoint,
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  };
}
